import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Project } from '../../models/project';
import { Constants } from '../../utils/constants';
import Swal from 'sweetalert2';
import { ProjectApiService } from '../../services/project.api.service';
import { SelectedProjectApiService } from '../selected-project/selected-project.api.service';
import { Subscription, switchMap, take, timer } from 'rxjs';
import { RunApiService } from '../../services/run.api.service';
import { ProjectPreviousRun } from '../../models/project-previous-run';
import { ColDef, DomLayoutType, RowGroupingDisplayType } from 'ag-grid-community';
import { GridComponent } from '../grid/grid.component';
import { PreviosRunsColdef } from './previous-runs.coldef';
import { ProjectXCompoundXModel } from '../../models/project-x-compound-x-model';
import { MetricsColdef } from './metrics-coldef';
import { ProjectMetrics } from '../../models/project-metrics';

@Component({
  selector: 'app-project-status',
  templateUrl: './project-status.component.html',
  styleUrls: ['./project-status.component.css']
})
export class ProjectStatusComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('grid') private grid!: GridComponent;
  @Input() selectedProject?: Project;
  @Input() selectedProjectStatus?: number;
  statusId: number = -1;
  statusName: string = "";
  badgeStyle: string = "";
  models: any[] = [];
  isAllFilesDownloading: boolean = false;
  isSummaryDownloading: boolean = false;
  isStoping: boolean = false;
  subscriptionStatus!: Subscription;
  subscriptionRunsSaved!: Subscription;
  subscriptionMetrics!: Subscription;
  downloadOutputFilesVisible: boolean = false;
  metricsVisible: boolean = false;
  previousRunRowData: any[] = [];
  metricsRowData: any[] = [];
  public rowSelection: any | "single" | "multiple" = {
    mode: "singleRow",
  };
  groupDisplayType: RowGroupingDisplayType = "multipleColumns";
  downloadAllFiles: boolean = false;
  paginationPageSize: number = 5;
  metricsPaginationPageSize: number = 5;

  gridStyle: any = {
    width: 'auto',
    height: '300px'
  };

  metricsGridStyle: any = {
    width: 'auto',
    height: '350px'
  };

  autoGroupMetricsColumnDef: ColDef = {
    headerName: 'Output Folder',
    width: 155
  };

  autoGroupPreviusRunColumnDef: ColDef = {
    minWidth: 250,
    cellRenderer: 'agGroupCellRenderer',
    cellRendererParams: {
      checkbox: true,
    },
  };

  projectMetrics: ProjectMetrics[] = [];
  projectElapsedTime: string | undefined;
  showLabel: boolean = false;
  public domLayout: DomLayoutType = "normal";

  constructor(private projectService: ProjectApiService,
    private selectedProjectApiService: SelectedProjectApiService,
    public runApiService: RunApiService,
    public previosRunsColdef: PreviosRunsColdef,
    public metricsColdef: MetricsColdef) { }

  columnDefs: ColDef[] = this.previosRunsColdef.getColumnsDefinition();
  metricsColumnDefs: ColDef[] = this.metricsColdef.getColumnsDefinition();

  refresh(): boolean {
    return false;
  }

  ngOnInit(): void {
    this.statusId = this.selectedProject?.status!;
    this.selectedProjectApiService.updateSelectedProjectStatus(this.selectedProject!.status!);
    this.getPreviousRuns(this.selectedProject);
    this.statusStyle(this.statusId ?? 0);
    this.monitorProjectStatus();
    this.selectedProject?.projectXCompoundXModel?.find((x: ProjectXCompoundXModel) => Constants.PWC_VERSIONS.includes(x.ModelName!))

      ?
      timer(50000).subscribe(() => {
        this.monitorRunsHaveBeenSavedPwc()
      }) :
      timer(30000).subscribe(() => {
        this.monitorRunsHaveBeenSaved()
      });
  }

  ngOnDestroy() {
    this.unsubscribeSubscriptions();
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    if (simpleChanges['selectedProject']) {
      this.statusId = this.selectedProject?.status!;
      this.statusStyle(this.statusId ?? 0);
    };
  }

  statusStyle(statusId: number) {
    switch (statusId) {
      case Constants.PROJECT_STATUS_ID.NOT_STARTED:
        this.statusName = Constants.PROJECT_STATUS.NOT_STARTED;
        this.badgeStyle = '';
        break;
      case Constants.PROJECT_STATUS_ID.IN_PROGRESS:
        this.statusName = Constants.PROJECT_STATUS.IN_PROGRESS.concat("\u00A0");
        this.badgeStyle = Constants.BADGE_STYLE.IN_PROGRESS;
        break;
      case Constants.PROJECT_STATUS_ID.SUCCESSFUL:
        this.statusName = Constants.PROJECT_STATUS.SUCCESSFUL.concat("\u00A0");
        this.badgeStyle = Constants.BADGE_STYLE.SUCCESSFUL;
        break;
      case Constants.PROJECT_STATUS_ID.FAILED:
        this.statusName = Constants.PROJECT_STATUS.FAILED.concat("\u00A0");
        this.badgeStyle = Constants.BADGE_STYLE.FAILED;
        break;
      case Constants.PROJECT_STATUS_ID.IN_QUEUE:
        this.statusName = Constants.PROJECT_STATUS.IN_QUEUE.concat("\u00A0");
        this.badgeStyle = Constants.BADGE_STYLE.IN_QUEUE;
        break;
      case Constants.PROJECT_STATUS_ID.STOPPED:
        this.statusName = Constants.PROJECT_STATUS.STOPPED;
        this.badgeStyle = '';
        break;
      case Constants.PROJECT_STATUS_ID.PARTIALLY_SUCCESSFUL:
        this.statusName = Constants.PROJECT_STATUS.PARTIALLY_SUCCESSFUL.concat("\u00A0");
        this.badgeStyle = Constants.BADGE_STYLE.PARTIALLY_SUCCESSFUL;
        break;
      case Constants.PROJECT_STATUS_ID.INSUFFICIENT_RESOURCES:
        this.statusName = Constants.PROJECT_STATUS.INSUFFICIENT_RESOURCES.concat("\u00A0");
        this.badgeStyle = Constants.BADGE_STYLE.INSUFFICIENT_RESOURCES;
        break;
      default:
        this.badgeStyle = '';
        break;
    }
  }

  showSummaryDownloadButton(): boolean | undefined {
    return this.selectedProject?.previousRuns && this.selectedProject.previousRuns.filter(run => run.status === Constants.PROJECT_STATUS_ID.SUCCESSFUL).length > 0;
  }

  showAllFilesDownloadButton(): boolean {
    return !!this.selectedProject?.previousRuns && this.selectedProject.previousRuns.length > 0;
  }

  showStopProjectButton(): boolean {
    return this.statusName === Constants.PROJECT_STATUS.IN_PROGRESS.concat("\u00A0");
  }

  onDownloadOutputFilesClick(pDownloadAllFiles: boolean = true): void {
    this.projectService.getPreviousRunsByProject(this.selectedProject?.projectPk, this.selectedProjectApiService.featureAcronym!).subscribe({
      next: (projectPreviousRun: ProjectPreviousRun[]) => {
        if (this.selectedProject) {
          this.selectedProject.previousRuns = this.getPreviousRunsByDownloadSelection(projectPreviousRun, pDownloadAllFiles);
          if (this.selectedProject.previousRuns.length > 0) {
            this.downloadOutputFilesVisible = true;
            this.downloadAllFiles = pDownloadAllFiles;
          }
          else {
            this.showErrorAlert("There are no files to download")
          }
        }
        this.previousRunRowData = this.selectedProject?.previousRuns!;
      },
      error: (error: Error) => {
        console.error(error);
      }
    });
  }

  getPreviousRunsByDownloadSelection(projectPreviousRun: ProjectPreviousRun[], pDownloadAllFiles: boolean): ProjectPreviousRun[] {
    const allFilesStatus = [
      Constants.PROJECT_STATUS_ID.SUCCESSFUL,
      Constants.PROJECT_STATUS_ID.FAILED
    ];
    const summaryFilesStatus = [
      Constants.PROJECT_STATUS_ID.SUCCESSFUL
    ];

    if (pDownloadAllFiles)
      return projectPreviousRun.filter(run => allFilesStatus.includes(run.status!));
    return projectPreviousRun.filter(run => summaryFilesStatus.includes(run.status!));
  }

  onDownloadSelectedRowsClick() {
    this.downloadOutputFilesVisible = false;
    let outpuFolders = this.grid.gridApi.getSelectedNodes().map((run: any) => run.key).flat();
    this.downloadOutputFilesByOutputFolders(outpuFolders, this.downloadAllFiles);
  }

  downloadOutputFilesByOutputFolders(outpuFolders: any, pDownloadAllFiles: boolean = true): void {
    this.setButtonLoading(pDownloadAllFiles);
    this.projectService.downloadOutputFilesByOutputFolders(outpuFolders, pDownloadAllFiles)
      .subscribe({
        next: (urls) => {
          this.setButtonLoading(pDownloadAllFiles);
          if (urls != null && urls.length > 0) {
            urls.forEach((url: string) => {
              window.open(url, '_blank');
            });
          }
        },
        error: (error) => {
          console.error(error);
          this.setButtonLoading(pDownloadAllFiles);
          this.showErrorAlert("Files could not be downloaded")
        },
      });
  }

  onStopProjectClick(): void {
    if (this.selectedProject) {
      this.unsubscribeSubscriptions();
      this.isStoping = true;
      this.selectedProject!.featureAcronym = this.selectedProjectApiService.featureAcronym;
      this.projectService.stopProject(this.selectedProject).subscribe({
        next: () => {
          const status = Constants.PROJECT_STATUS_ID.STOPPED;
          this.selectedProject!.status = status;
          this.statusId = status;
          this.isStoping = false;
          this.statusStyle(this.statusId ?? Constants.PROJECT_STATUS_ID.STOPPED);
          this.selectedProjectApiService.updateSelectedProjectStatus(this.selectedProject!.status);
          this.onMetricsClick(false);
          this.unsubscribeSubscriptions();
        },
        error: (error) => {
          this.isStoping = false;
          console.error(error);
        },
      })
    }
  }

  monitorProjectStatus(): void {
    this.subscriptionStatus = timer(0, 30000).pipe(
      switchMap(() => this.projectService.getProjectStatus(this.selectedProject!.projectPk, this.selectedProjectApiService.featureAcronym!).pipe(take(1)))
    ).subscribe({
      next: (status) => {
        this.selectedProject!.status = status;
        this.statusId = this.selectedProject?.status!;
        this.statusStyle(this.statusId ?? status);
        this.selectedProjectApiService.updateSelectedProjectStatus(this.selectedProject!.status);
        this.onMetricsClick(this.metricsVisible);
        if (this.selectedProject!.status == Constants.PROJECT_STATUS_ID.STOPPED ||
          this.selectedProject!.status == Constants.PROJECT_STATUS_ID.FAILED ||
          this.selectedProject!.status == Constants.PROJECT_STATUS_ID.SUCCESSFUL ||
          this.selectedProject!.status == Constants.PROJECT_STATUS_ID.NOT_STARTED ||
          this.selectedProject!.status == Constants.PROJECT_STATUS_ID.PARTIALLY_SUCCESSFUL) {
          this.getPreviousRuns(this.selectedProject);
          this.unsubscribeSubscriptions();
        }
      },
      error: (error) => {
        console.error(error)
      }
    });
  }

  monitorProjectStatusPWC(): void {
    this.subscriptionStatus = timer(0, 30000).pipe(
      switchMap(() => this.projectService.getProjectStatus(this.selectedProject!.projectPk, this.selectedProjectApiService.featureAcronym!).pipe(take(1)))
    ).subscribe({
      next: (status) => {
        this.selectedProject!.status = status;
        this.statusId = this.selectedProject?.status!;
        this.statusStyle(this.statusId ?? status);
        this.selectedProjectApiService.updateSelectedProjectStatus(this.selectedProject!.status);
        if (this.selectedProject!.status === Constants.PROJECT_STATUS_ID.IN_PROGRESS ||
          this.selectedProject!.status === Constants.PROJECT_STATUS_ID.STOPPED ||
          this.selectedProject!.status == Constants.PROJECT_STATUS_ID.SUCCESSFUL)
          this.onMetricsClick(this.metricsVisible);
        this.getPreviousRuns(this.selectedProject);
      },
      error: (error) => {
        console.warn(error)
      }
    });
  }

  getPreviousRuns(selectedProject: Project | undefined) {
    const validStatus = [
      Constants.PROJECT_STATUS_ID.SUCCESSFUL,
      Constants.PROJECT_STATUS_ID.FAILED
    ];

    this.projectService.getPreviousRunsByProject(selectedProject?.projectPk, this.selectedProjectApiService.featureAcronym!).subscribe({
      next: (projectPreviousRun: ProjectPreviousRun[]) => {
        if (selectedProject) {
          selectedProject.previousRuns = projectPreviousRun.filter(run => validStatus.includes(run.status!));
          this.previousRunRowData = selectedProject.previousRuns;
        }
      },
      error: (error: Error) => {
        console.error(error)
      }
    });
  }

  monitorRunsHaveBeenSaved(): void {
    this.subscriptionRunsSaved = timer(0, 30000).pipe(
      switchMap(() => this.runApiService.runsHaveBeenSaved(this.selectedProject!.projectPk).pipe(take(1)))
    ).subscribe({
      next: (status) => {
        this.selectedProject!.runsSaved = status;
        if (status) {
          this.selectedProjectApiService.updateRunsHaveBeenSaved(this.selectedProject!.runsSaved);
          this.subscriptionRunsSaved.unsubscribe();
        }
      },
      error: (error) => {
        console.error(error)
      }
    });
  }

  monitorRunsHaveBeenSavedPwc(): void {
    this.subscriptionRunsSaved = timer(0, 30000).pipe(
      switchMap(() => this.runApiService.runsHaveBeenSavedPwc(this.selectedProject!.projectPk).pipe(take(1)))
    ).subscribe({
      next: (status) => {
        this.selectedProject!.runsSaved = status;
        if (status) {
          this.selectedProjectApiService.updateRunsHaveBeenSaved(this.selectedProject!.runsSaved);
          this.subscriptionRunsSaved.unsubscribe();
        }
      },
      error: (error) => {
        console.warn(error)
      }
    });
  }

  setButtonLoading(pDownloadAllFiles: boolean = true): void {
    if (pDownloadAllFiles) {
      this.isAllFilesDownloading = !this.isAllFilesDownloading;
    } else {
      this.isSummaryDownloading = !this.isSummaryDownloading;
    }
  }

  unsubscribeSubscriptions() {
    if (this.subscriptionStatus)
      this.subscriptionStatus.unsubscribe();
    if (this.subscriptionRunsSaved)
      this.subscriptionRunsSaved.unsubscribe();
    if (this.subscriptionMetrics)
      this.subscriptionMetrics.unsubscribe();
  }

  showErrorAlert(text: string) {
    Swal.fire({
      title: 'Notification',
      html: text,
      confirmButtonColor: '#0069be',
      confirmButtonText: 'OK',
    }).then((result) => {

    });
  }

  createProjectObject(data: any, pDownloadAllFiles: boolean = true): Project {
    return {
      projectPk: data.projectPk,
      active: data.active,
      createdBy: data.createdBy,
      deletable: data.deletable,
      description: data.description,
      formulationPk: data.formulationPk,
      name: data.name,
      status: data.status,
      downloadAllFiles: pDownloadAllFiles,
      createdDate: data.createdDate,
      featureAcronym: this.selectedProjectApiService.featureAcronym
    } as Project;
  }

  onMetricsClick(metricsVisible: boolean = true): void {
    this.projectService.getMetricsByProject(this.selectedProject!.projectPk, this.selectedProjectApiService.featureAcronym!).pipe(take(1))
      .subscribe({
        next: (projectMetrics) => {
          this.processMetrics(projectMetrics);
          this.metricsVisible = metricsVisible;
        },
        error: (error) => {
          console.error(error)
          this.metricsVisible = false;
        }
      });
  }

  processMetrics(projectMetrics: ProjectMetrics[]): ProjectMetrics {
    this.metricsRowData = projectMetrics.filter(pm => pm.type === 'Model').map(metric => {
      const modelInfo = this.selectedProject?.projectXCompoundXModel?.find(
        (x: ProjectXCompoundXModel) => {
          if (Constants.PWC_VERSIONS.includes(metric.name) &&
            Constants.PWC_VERSIONS.includes(x.ModelName!)) {
            return true;
          }
          const modelNameMatch = x.ModelName === metric.name ||
            (x as any).modelName === metric.name;
          return modelNameMatch;
        }
      );


      if (modelInfo) {
        const modelInfoAny = modelInfo as any;
        const modelVersion = modelInfo.ModelVersion ??
          modelInfoAny.modelVersion ??
          modelInfoAny.Version ??
          modelInfoAny.version;
        if (modelVersion) {
          metric.ModelVersion = String(modelVersion);
        }
      }
      return metric;
    });
    this.showLabel = this.metricsRowData.find(pm => pm.name === 'PRZM');
    var project = projectMetrics.find(pm => pm.type === 'Project');
    this.projectElapsedTime = project?.FormattedElapsedTime;
    return project ?? {} as ProjectMetrics;
  }

  showMetricsButton() {
    return this.metricsRowData != undefined && this.metricsRowData.length > 0;
  }

  showElapsedTime() {
    return this.projectElapsedTime && (this.selectedProject!.status === Constants.PROJECT_STATUS_ID.IN_PROGRESS || this.selectedProject!.status === Constants.PROJECT_STATUS_ID.STOPPED ||
      this.selectedProject!.status === Constants.PROJECT_STATUS_ID.SUCCESSFUL || this.selectedProject!.status === Constants.PROJECT_STATUS_ID.PARTIALLY_SUCCESSFUL)
  }

  showInfoPopup() {
    Swal.fire({
      title: 'Information',
      html: Constants.MESSAGES.METRICS,
      icon: 'info',
      width: 425
    });
  }
}